import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import styles from './Macro.module.css';
import { fetchRecorrencia, fetchRecorrenciaCliente, registrarContatoRecorrencia } from '../../services/api';

const STATUS_BADGE = {
    em_dia:         { label: 'Em dia',          bg: '#dcfce7', color: '#15803d', icon: '🟢' },
    atrasado:       { label: 'Atrasado',        bg: '#fef9c3', color: '#854d0e', icon: '🟡' },
    quebrou_padrao: { label: 'Quebrou padrão',  bg: '#fee2e2', color: '#991b1b', icon: '🔴' }
};

const CAT_STATUS_BADGE = {
    em_dia:         { label: 'Em dia',         bg: '#dcfce7', color: '#15803d' },
    atrasada:       { label: 'Atrasada',       bg: '#fef9c3', color: '#854d0e' },
    quebrou:        { label: 'Quebrou padrão', bg: '#fee2e2', color: '#991b1b' },
    em_observacao:  { label: 'Em observação',  bg: '#f1f5f9', color: '#475569' },
    esporadico:     { label: 'Esporádico',     bg: '#f8fafc', color: '#94a3b8' },
};

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

export default function RecorrenciaTab() {
    const [statusFiltro, setStatusFiltro] = useState('quebrou_padrao');
    const [busca, setBusca] = useState('');
    const [buscaDebounced, setBuscaDebounced] = useState('');
    const [dados, setDados] = useState({ items: [], total: 0, resumo: {} });
    const [loading, setLoading] = useState(false);
    const [detalheCliente, setDetalheCliente] = useState(null);
    const [modalContato, setModalContato] = useState(null);
    const [formContato, setFormContato] = useState({ canal: 'whatsapp', resultado: 'sem_resposta', observacao: '' });
    const [kpiCategoria, setKpiCategoria] = useState(null);  // { com_categoria_quebrada, receita_perdida_total, top_categorias_afetadas }
    const [kpiCarregando, setKpiCarregando] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setBuscaDebounced(busca.trim()), 350);
        return () => clearTimeout(t);
    }, [busca]);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        fetchRecorrencia({ status: statusFiltro, q: buscaDebounced, limit: 300 })
            .then(r => { if (!cancel) setDados(r); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [statusFiltro, buscaDebounced]);

    // KPI agregado: clientes com ≥1 categoria de produto quebrada.
    // Backend cacheia 10min. Carrega só 1x ao montar.
    useEffect(() => {
        let cancel = false;
        setKpiCarregando(true);
        api.get('/api/recorrencia/kpi-categoria-quebrada')
            .then(r => { if (!cancel) setKpiCategoria(r.data); })
            .catch(() => {})
            .finally(() => { if (!cancel) setKpiCarregando(false); });
        return () => { cancel = true; };
    }, []);

    const resumo = dados.resumo || {};

    const abrirDetalhe = async (cliente) => {
        setDetalheCliente({ ...cliente, _loading: true });
        const r = await fetchRecorrenciaCliente(cliente.cliente_chave);
        setDetalheCliente({
            ...cliente,
            pedidos: r.pedidos || [],
            contatos: r.contatos || [],
            categorias: r.categorias || [],
            diagnostico: r.diagnostico || '',
            receita_perdida_estimada: r.receita_perdida_estimada || 0,
            categorias_quebradas: r.categorias_quebradas || 0,
            categorias_atrasadas: r.categorias_atrasadas || 0,
            dica: null,         // só carrega quando clicar
            dicaLoading: false,
            dicaErro: null,
            _loading: false,
        });
    };

    const pedirDicaAtacado = async () => {
        if (!detalheCliente) return;
        setDetalheCliente({ ...detalheCliente, dicaLoading: true, dicaErro: null });
        try {
            const r = await api.get(`/api/recorrencia/cliente/${encodeURIComponent(detalheCliente.cliente_chave)}/dica-atacado`);
            setDetalheCliente((prev) => prev && prev.cliente_chave === detalheCliente.cliente_chave
                ? { ...prev, dica: r.data?.dica, dicaLoading: false }
                : prev
            );
        } catch (e) {
            setDetalheCliente((prev) => prev && prev.cliente_chave === detalheCliente.cliente_chave
                ? { ...prev, dicaLoading: false, dicaErro: e.response?.data?.error || e.message }
                : prev
            );
        }
    };

    const abrirContato = (cliente) => {
        setFormContato({ canal: cliente.telefone ? 'whatsapp' : 'telefone', resultado: 'sem_resposta', observacao: '' });
        setModalContato(cliente);
    };

    const enviarContato = async () => {
        if (!modalContato) return;
        try {
            await registrarContatoRecorrencia({
                cliente_chave: modalContato.cliente_chave,
                cliente_nome: modalContato.nome,
                ...formContato
            });
            setModalContato(null);
            // Recarrega lista pra mostrar o contato registrado
            const r = await fetchRecorrencia({ status: statusFiltro, q: buscaDebounced, limit: 300 });
            setDados(r);
        } catch (e) {
            alert('Erro ao registrar contato: ' + (e.response?.data?.error || e.message));
        }
    };

    return (
        <div>
            {/* Resumo */}
            <div className={styles.kpiGrid} style={{ marginBottom: 16 }}>
                <div className={`${styles.kpiCard} ${styles.info}`} onClick={() => setStatusFiltro('todos')} style={{ cursor: 'pointer' }}>
                    <div className={styles.kpiLabel}>Recorrentes (3+ compras / 12m)</div>
                    <div className={styles.kpiValue}>{resumo.total_recorrentes || 0}</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.success}`} onClick={() => setStatusFiltro('em_dia')} style={{ cursor: 'pointer' }}>
                    <div className={styles.kpiLabel}>🟢 Em dia</div>
                    <div className={styles.kpiValue}>{resumo.em_dia || 0}</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.warning}`} onClick={() => setStatusFiltro('atrasado')} style={{ cursor: 'pointer' }}>
                    <div className={styles.kpiLabel}>🟡 Atrasados</div>
                    <div className={styles.kpiValue}>{resumo.atrasado || 0}</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.danger}`} onClick={() => setStatusFiltro('quebrou_padrao')} style={{ cursor: 'pointer' }}>
                    <div className={styles.kpiLabel}>🔴 Quebraram padrão</div>
                    <div className={styles.kpiValue}>{resumo.quebrou_padrao || 0}</div>
                </div>
            </div>

            {/* KPI agregado: clientes com categoria-chave quebrada */}
            {kpiCategoria && !kpiCategoria.sem_dados && kpiCategoria.com_categoria_quebrada > 0 && (
                <div style={{
                    background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)',
                    border: '1px solid #f97316', borderRadius: 10, padding: 14, marginBottom: 16,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
                }}>
                    <div>
                        <div style={{ fontSize: 12, color: '#9a3412', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            🎯 Detecção por categoria de produto
                        </div>
                        <div style={{ fontSize: 18, color: '#7c2d12', fontWeight: 700, marginTop: 4 }}>
                            <strong>{kpiCategoria.com_categoria_quebrada}</strong> clientes com queda em ≥1 categoria-chave ·{' '}
                            receita perdida estimada{' '}
                            <strong>{fmtBRL(kpiCategoria.receita_perdida_total)}</strong>
                        </div>
                        {kpiCategoria.top_categorias_afetadas?.length > 0 && (
                            <div style={{ fontSize: 12, color: '#7c2d12', marginTop: 6 }}>
                                Top categorias afetadas:{' '}
                                {kpiCategoria.top_categorias_afetadas.map((c, i) => (
                                    <span key={c.categoria} style={{ background: '#fff', padding: '2px 8px', borderRadius: 10, marginRight: 6, fontWeight: 600 }}>
                                        {c.categoria} ({c.clientes})
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {kpiCarregando && (
                <div style={{ background: '#f8fafc', padding: 10, borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                    Analisando categorias dos clientes em quebrou_padrao... (~30s)
                </div>
            )}

            {/* Filtros */}
            <div className={styles.tableCard}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}>
                        <option value="todos">Todos os recorrentes</option>
                        <option value="em_dia">🟢 Em dia</option>
                        <option value="atrasado">🟡 Atrasados</option>
                        <option value="quebrou_padrao">🔴 Quebraram padrão</option>
                    </select>
                    <input
                        type="text"
                        placeholder="🔍 Buscar nome, email ou telefone…"
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', flex: '1 1 240px', minWidth: 200 }}
                    />
                    <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
                        {loading ? 'Carregando…' : `${dados.total || 0} clientes`}
                    </span>
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Cidade/UF</th>
                                <th style={{ textAlign: 'right' }}>Pedidos<br/><small>(12m)</small></th>
                                <th style={{ textAlign: 'right' }}>Valor 12m</th>
                                <th>Último pedido</th>
                                <th style={{ textAlign: 'right' }}>Dias sem comprar</th>
                                <th>Status</th>
                                <th>Último contato</th>
                                <th style={{ textAlign: 'center' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dados.items?.map(c => {
                                const badge = STATUS_BADGE[c.status_padrao] || STATUS_BADGE.em_dia;
                                return (
                                    <tr key={c.cliente_chave}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{c.nome || c.email || c.cliente_chave}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                                {c.email}{c.telefone ? ` · 📱 ${c.telefone}` : ''}
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13 }}>{c.cidade || '—'}{c.estado ? `/${c.estado}` : ''}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{c.n_pedidos_12m}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtBRL(c.valor_total_12m)}</td>
                                        <td style={{ fontSize: 13 }}>{fmtDate(c.ultimo_pedido)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700,
                                            color: c.dias_desde_ultimo > 120 ? '#991b1b'
                                                  : c.dias_desde_ultimo > 60 ? '#854d0e'
                                                  : '#15803d' }}>
                                            {c.dias_desde_ultimo}d
                                        </td>
                                        <td>
                                            <span style={{ background: badge.bg, color: badge.color, padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                {badge.icon} {badge.label}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 11, color: '#475569' }}>
                                            {c.ultimo_contato_em ? (
                                                <>
                                                    {fmtDate(c.ultimo_contato_em)}<br/>
                                                    <span style={{ opacity: 0.7 }}>por {c.ultimo_contato_por || '—'}</span>
                                                </>
                                            ) : <em style={{ opacity: 0.6 }}>nunca</em>}
                                        </td>
                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {c.whatsapp_link && (
                                                <a href={c.whatsapp_link} target="_blank" rel="noopener noreferrer"
                                                    title="Falar no WhatsApp"
                                                    style={{ display: 'inline-block', padding: '4px 8px', background: '#25d366', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 12, marginRight: 4 }}>
                                                    💬 WhatsApp
                                                </a>
                                            )}
                                            <button onClick={() => abrirContato(c)}
                                                style={{ padding: '4px 8px', background: '#6c5ce7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>
                                                ✅ Contatado
                                            </button>
                                            <button onClick={() => abrirDetalhe(c)}
                                                style={{ padding: '4px 8px', background: '#fff', color: '#1f2937', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                                📋 Ver
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && dados.items?.length === 0 && (
                                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>Nenhum cliente nesse filtro.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal detalhe — timeline + histórico */}
            {detalheCliente && (
                <div onClick={() => setDetalheCliente(null)} style={modalOverlayStyle}>
                    <div onClick={e => e.stopPropagation()} style={{ ...modalContentStyle, maxWidth: 760 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{detalheCliente.nome || detalheCliente.email}</h3>
                                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                                    {detalheCliente.email} {detalheCliente.telefone ? `· 📱 ${detalheCliente.telefone}` : ''}<br/>
                                    {detalheCliente.cidade}{detalheCliente.estado ? `/${detalheCliente.estado}` : ''}
                                </div>
                            </div>
                            <button onClick={() => setDetalheCliente(null)} style={closeBtnStyle}>✕</button>
                        </div>

                        <div style={{ display: 'flex', gap: 16, padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                            <div><strong>{detalheCliente.n_pedidos_12m}</strong> pedidos / 12m</div>
                            <div><strong>{fmtBRL(detalheCliente.valor_total_12m)}</strong> em compras</div>
                            <div>Último: <strong>{fmtDate(detalheCliente.ultimo_pedido)}</strong></div>
                        </div>

                        {detalheCliente._loading && <div>Carregando histórico…</div>}

                        {!detalheCliente._loading && (
                            <>
                                {/* Banner diagnóstico */}
                                {detalheCliente.diagnostico && (
                                    <div style={{
                                        background: detalheCliente.categorias_quebradas > 0 ? '#fef2f2'
                                                  : detalheCliente.categorias_atrasadas > 0 ? '#fffbeb'
                                                  : '#f0fdf4',
                                        border: `1px solid ${detalheCliente.categorias_quebradas > 0 ? '#fecaca'
                                                  : detalheCliente.categorias_atrasadas > 0 ? '#fde68a'
                                                  : '#bbf7d0'}`,
                                        borderRadius: 8, padding: 12, marginBottom: 16,
                                        fontSize: 13, lineHeight: 1.4,
                                        color: detalheCliente.categorias_quebradas > 0 ? '#7f1d1d'
                                             : detalheCliente.categorias_atrasadas > 0 ? '#78350f'
                                             : '#14532d',
                                    }}>
                                        <div style={{ fontWeight: 600 }}>{detalheCliente.diagnostico}</div>
                                    </div>
                                )}

                                {/* Card do especialista em atacado */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    border: '1px solid #f59e0b',
                                    borderRadius: 10, padding: 14, marginBottom: 16,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <div style={{ fontWeight: 700, color: '#78350f', fontSize: 13 }}>
                                            💡 Especialista em Vendas no Atacado
                                            {detalheCliente.dica?._cached && (
                                                <span style={{ fontSize: 10, fontWeight: 500, marginLeft: 8, opacity: 0.7 }}>· cache 24h</span>
                                            )}
                                        </div>
                                        {!detalheCliente.dica && !detalheCliente.dicaLoading && (
                                            <button onClick={pedirDicaAtacado} style={{
                                                background: '#78350f', color: '#fff', border: 'none', borderRadius: 6,
                                                padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                            }}>
                                                Pedir dica de venda
                                            </button>
                                        )}
                                        {detalheCliente.dica && (
                                            <button onClick={pedirDicaAtacado} title="Regenerar"
                                                style={{ background: 'transparent', color: '#78350f', border: '1px solid #f59e0b',
                                                         borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>
                                                🔄 Regenerar
                                            </button>
                                        )}
                                    </div>

                                    {detalheCliente.dicaLoading && (
                                        <div style={{ marginTop: 12, fontSize: 12, color: '#78350f', fontStyle: 'italic' }}>
                                            Analisando perfil e gerando dicas… (até 20s)
                                        </div>
                                    )}

                                    {detalheCliente.dicaErro && (
                                        <div style={{ marginTop: 12, fontSize: 12, color: '#991b1b',
                                                      background: '#fef2f2', padding: 8, borderRadius: 6 }}>
                                            Falha ao gerar dica: {detalheCliente.dicaErro}
                                        </div>
                                    )}

                                    {detalheCliente.dica?.resumo_perfil && (
                                        <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.6)',
                                                      borderRadius: 6, fontSize: 12, color: '#78350f', fontStyle: 'italic' }}>
                                            📌 {detalheCliente.dica.resumo_perfil}
                                        </div>
                                    )}

                                    {detalheCliente.dica?.dicas?.length > 0 && (
                                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {detalheCliente.dica.dicas.map((d, i) => (
                                                <div key={i} style={{
                                                    background: '#fff', padding: '10px 12px', borderRadius: 8,
                                                    borderLeft: '4px solid #f59e0b',
                                                }}>
                                                    <div style={{ fontWeight: 700, color: '#78350f', fontSize: 13, marginBottom: 4 }}>
                                                        {i + 1}. {d.titulo}
                                                    </div>
                                                    <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.4 }}>
                                                        {d.acao}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Análise por categoria de produto */}
                                {detalheCliente.categorias?.length > 0 && (
                                    <>
                                        <h4 style={{ fontSize: 14, marginTop: 0 }}>
                                            Análise por categoria
                                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>
                                                · derivada do prefixo do SKU · status por dias sem comprar
                                            </span>
                                        </h4>
                                        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 16 }}>
                                            <table style={{ width: '100%', fontSize: 12 }}>
                                                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                                                    <tr>
                                                        <th style={{ textAlign: 'left', padding: '6px 10px' }}>Categoria</th>
                                                        <th style={{ textAlign: 'left', padding: '6px 10px' }}>Status</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Compras</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Última<br/>compra</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Sem<br/>comprar há</th>
                                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Total<br/>R$</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {detalheCliente.categorias.map(c => {
                                                        const badge = CAT_STATUS_BADGE[c.status_categoria] || CAT_STATUS_BADGE.esporadico;
                                                        return (
                                                            <tr key={c.categoria}>
                                                                <td style={{ padding: '6px 10px', fontWeight: 600 }}>{c.categoria}</td>
                                                                <td style={{ padding: '6px 10px' }}>
                                                                    <span style={{ background: badge.bg, color: badge.color, padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                                                                        {badge.label}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{c.ocorrencias}</td>
                                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtDate(c.ultima_compra)}</td>
                                                                <td style={{ padding: '6px 10px', textAlign: 'right',
                                                                    color: c.status_categoria === 'quebrou' ? '#991b1b' : c.status_categoria === 'atrasada' ? '#854d0e' : '#64748b',
                                                                    fontWeight: c.status_categoria === 'quebrou' || c.status_categoria === 'atrasada' ? 700 : 400 }}>
                                                                    {c.dias_desde_ultima}d
                                                                </td>
                                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtBRL(c.valor_total)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}

                                {/* Gráfico de compras por mês */}
                                <ChartComprasMes pedidos={detalheCliente.pedidos} />

                                <h4 style={{ fontSize: 14, marginTop: 16 }}>Histórico de pedidos ({detalheCliente.pedidos?.length || 0})</h4>
                                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 16 }}>
                                    {detalheCliente.pedidos?.map(p => (
                                        <div key={`${p.origem}-${p.pedido_id}`} style={{
                                            padding: '10px 12px', borderBottom: '1px solid #f1f5f9',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
                                        }}>
                                            <div>
                                                <strong>{fmtDate(p.data_pedido)}</strong>{' · '}
                                                <span style={{ fontSize: 10, background: p.origem === 'tray' ? '#dbeafe' : '#dcfce7', color: p.origem === 'tray' ? '#1e40af' : '#15803d', padding: '2px 6px', borderRadius: 4 }}>
                                                    {p.origem.toUpperCase()}
                                                </span>
                                                {' · '}<span style={{ color: '#64748b', fontSize: 12 }}>{p.status}</span>
                                            </div>
                                            <div style={{ fontWeight: 700 }}>{fmtBRL(p.valor_total)}</div>
                                        </div>
                                    ))}
                                </div>

                                <h4 style={{ fontSize: 14 }}>Contatos registrados ({detalheCliente.contatos?.length || 0})</h4>
                                {detalheCliente.contatos?.length === 0
                                    ? <div style={{ color: '#94a3b8', fontSize: 12 }}>Nenhum contato registrado ainda.</div>
                                    : (
                                        <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                                            {detalheCliente.contatos.map(ct => (
                                                <div key={ct.id} style={{ padding: 8, borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>
                                                    <div><strong>{ct.usuario_nome || '?'}</strong> · {fmtDate(ct.contatado_em)} · {ct.canal} → <em>{ct.resultado}</em></div>
                                                    {ct.observacao && <div style={{ marginTop: 2, color: '#475569' }}>{ct.observacao}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                }
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal registrar contato */}
            {modalContato && (
                <div onClick={() => setModalContato(null)} style={modalOverlayStyle}>
                    <div onClick={e => e.stopPropagation()} style={{ ...modalContentStyle, maxWidth: 480 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}>Registrar contato</h3>
                            <button onClick={() => setModalContato(null)} style={closeBtnStyle}>✕</button>
                        </div>
                        <div style={{ fontSize: 13, marginBottom: 12 }}>
                            <strong>{modalContato.nome || modalContato.email}</strong>
                            {modalContato.telefone && <span> · 📱 {modalContato.telefone}</span>}
                        </div>

                        <label style={labelStyle}>Canal:</label>
                        <select value={formContato.canal} onChange={e => setFormContato({ ...formContato, canal: e.target.value })} style={inputStyle}>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="telefone">Telefone</option>
                            <option value="email">Email</option>
                            <option value="presencial">Presencial</option>
                            <option value="outro">Outro</option>
                        </select>

                        <label style={labelStyle}>Resultado:</label>
                        <select value={formContato.resultado} onChange={e => setFormContato({ ...formContato, resultado: e.target.value })} style={inputStyle}>
                            <option value="comprou">Comprou</option>
                            <option value="vai_comprar">Vai comprar</option>
                            <option value="sem_interesse">Sem interesse</option>
                            <option value="sem_resposta">Sem resposta</option>
                            <option value="outro">Outro</option>
                        </select>

                        <label style={labelStyle}>Observação (opcional):</label>
                        <textarea value={formContato.observacao} onChange={e => setFormContato({ ...formContato, observacao: e.target.value })}
                            rows={3} style={{ ...inputStyle, fontFamily: 'inherit' }}
                            placeholder="O que aconteceu, motivo da não compra, próxima ação…" />

                        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                            <button onClick={() => setModalContato(null)} style={{ ...btnStyle, background: '#fff', color: '#1f2937', border: '1px solid #cbd5e1' }}>Cancelar</button>
                            <button onClick={enviarContato} style={{ ...btnStyle, background: '#6c5ce7', color: '#fff' }}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Gráfico de pilares: pedidos por mês + soma de valor.
 * Cobre do 1º pedido até o mês atual, com gaps preenchidos como zero.
 */
function ChartComprasMes({ pedidos }) {
    const data = useMemo(() => {
        if (!pedidos?.length) return [];
        // Agrega por YYYY-MM (só meses com compra — sem gaps)
        const agg = {};
        for (const p of pedidos) {
            const d = new Date(p.data_pedido);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (!agg[key]) {
                agg[key] = {
                    key,
                    mes: `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`,
                    pedidos: 0,
                    valor: 0,
                };
            }
            agg[key].pedidos += 1;
            agg[key].valor += Number(p.valor_total) || 0;
        }
        return Object.values(agg)
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((r) => ({ mes: r.mes, pedidos: r.pedidos, valor: +r.valor.toFixed(2) }));
    }, [pedidos]);

    if (data.length === 0) return null;

    const valorMax = Math.max(...data.map((d) => d.valor));

    return (
        <>
            <h4 style={{ fontSize: 14, marginTop: 0, marginBottom: 8 }}>
                Compras por mês
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>
                    · {data.length} {data.length === 1 ? 'mês' : 'meses'} com compra
                </span>
            </h4>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ overflowX: data.length > 24 ? 'auto' : 'visible' }}>
                    <ResponsiveContainer width={data.length > 24 ? data.length * 32 : '100%'} height={220}>
                    <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} interval={0} angle={data.length > 12 ? -45 : 0} textAnchor={data.length > 12 ? 'end' : 'middle'} height={data.length > 12 ? 50 : 30} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                        <Tooltip
                            formatter={(v, name) => [
                                name === 'valor' ? fmtBRL(v) : `${v} pedido${v !== 1 ? 's' : ''}`,
                                name === 'valor' ? 'Valor' : 'Pedidos',
                            ]}
                            labelFormatter={(l) => `Mês: ${l}`}
                            contentStyle={{ fontSize: 12 }}
                        />
                        <Bar dataKey="valor" name="valor" radius={[4, 4, 0, 0]}>
                            {data.map((d, i) => (
                                <Cell key={i} fill={d.valor === valorMax && valorMax > 0 ? '#4c1d95' : d.valor > 0 ? '#6c5ce7' : '#e5e7eb'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginTop: 8, padding: '0 12px' }}>
                    <span>Total: <strong style={{ color: '#1f2937' }}>{fmtBRL(data.reduce((s, d) => s + d.valor, 0))}</strong></span>
                    <span>Pedidos: <strong style={{ color: '#1f2937' }}>{data.reduce((s, d) => s + d.pedidos, 0)}</strong></span>
                    <span>Meses com compra: <strong style={{ color: '#1f2937' }}>{data.length}</strong></span>
                </div>
            </div>
        </>
    );
}

const modalOverlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
};
const modalContentStyle = {
    background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
};
const closeBtnStyle = {
    border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#64748b', padding: 0
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginTop: 10, marginBottom: 4, color: '#475569' };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 };
const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
